import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { getFieldTimeSlots } from '@/services/fieldApiService';
import api from '@/services/apiService';
import { authService } from '@/services/authService';

interface AvailableSession {
  id: string;
  label: string;
  startTime: string; // HH:MM:SS
  endTime: string; // HH:MM:SS
}

interface ScheduleFormValues {
  teamName: string;
  whatsapp: string;
  description: string;
  bookingDate: string; // YYYY-MM-DD
  sessions: string[];
}

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddSchedule: (events: any[]) => void;
  availableSessions?: AvailableSession[]; // optional if fetching from API
  arenaSlug?: string;
  fieldSlug?: string;
}

const validationSchema = Yup.object({
  teamName: Yup.string().required('Nama tim wajib diisi'),
  bookingDate: Yup.string().required('Tanggal booking wajib diisi'),
  sessions: Yup.array().min(1, 'Pilih minimal satu sesi'),
});

const ScheduleModal: React.FC<ScheduleModalProps> = ({
  isOpen,
  onClose,
  onAddSchedule,
  availableSessions = [],
  arenaSlug,
  fieldSlug,
}) => {
  const [fetchedSessions, setFetchedSessions] = useState<AvailableSession[] | null>(null);
  const initialValues: ScheduleFormValues = {
    teamName: '',
    whatsapp: '',
    description: '',
    bookingDate: new Date().toISOString().slice(0, 10),
    sessions: [],
  };

  // fetch time slots helper
  const fetchSlots = async (date: string) => {
    if (!arenaSlug || !fieldSlug) return;
    try {
      const res = await getFieldTimeSlots(arenaSlug, fieldSlug, date);
      const mapped: AvailableSession[] = res.data.time_slots.map((t) => ({
        id: t.public_id || String(t.id),
        label: `${t.start_time}-${t.end_time}`,
        startTime: t.start_time,
        endTime: t.end_time,
      }));
      setFetchedSessions(mapped);
    } catch (err) {
      console.warn('Failed to fetch time slots', err);
      setFetchedSessions([]);
    }
  };

  // initial fetch when modal opens (today)
  useEffect(() => {
    if (!isOpen) return;
    const today = new Date().toISOString().slice(0, 10);
    fetchSlots(today);
  }, [isOpen]);

  const handleSubmit = async (values: ScheduleFormValues, { setSubmitting, resetForm }: any) => {
    setSubmitting(true);

    // use the same sessions source the form renders (fetchedSessions if present)
    const sessionSource = fetchedSessions ?? availableSessions;

    // build events locally (used for UI) but prepare payload for API
    const created: any[] = values.sessions
      .map((sessionId) => {
        const s = sessionSource.find((x) => x.id === sessionId);
        if (!s) return null;

        // build ISO datetimes
        const date = values.bookingDate; // YYYY-MM-DD
        const start = `${date}T${s.startTime}`;
        const end = `${date}T${s.endTime}`;

        return {
          id: Date.now().toString() + '-' + sessionId,
          title: values.teamName,
          start,
          end,
          extendedProps: { whatsapp: values.whatsapp, description: values.description },
          backgroundColor: '#f97316',
          borderColor: '#f97316',
          textColor: '#ffffff',
        };
      })
      .filter(Boolean) as any[];

    // prepare API payload
    const payload = {
      team_name: values.teamName,
      phone_number: values.whatsapp,
      description: values.description,
      field_slug: fieldSlug || '',
      booking_date: values.bookingDate,
      selected_slots: values.sessions,
    };

    try {
      // try mitra token first (for internal endpoints), fallback to client token
      const tokenMitra = authService.getToken?.() ?? null;
      const tokenClient = authService.getClientToken?.() ?? null;
      const token = tokenMitra || tokenClient;
      const arenaSlug = authService.getSlug();

      console.debug('Booking attempt', {
        payload,
        tokenPresent: !!token,
        usedTokenMitra: !!tokenMitra,
      });

      if (!token) throw new Error('Authentication required. Please log in.');

      const res = await api.post(`/arenas/${arenaSlug}/internal-booking`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.debug('Booking response', res?.status, res?.data);

      // consider success when response.success === true or HTTP 2xx
      if (res && res.status >= 200 && res.status < 300) {
        // notify parent with created events
        onAddSchedule(created);
        resetForm();
        onClose();
      } else {
        console.warn('Booking failed', res?.data || res);
        // You might want to surface an error toast here
      }
    } catch (err: any) {
      console.error('Failed to create booking', err);
      // You might want to show user-facing error here
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Schedule" size="md">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nama Tim</label>
              <Field
                name="teamName"
                placeholder="Tim Garuda"
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <ErrorMessage name="teamName" component="div" className="text-red-500 text-xs mt-1" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">No. Whatsapp</label>
              <Field
                name="whatsapp"
                placeholder="+62828919212"
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Deskripsi</label>
              <Field
                as="textarea"
                name="description"
                placeholder="Keterangan tambahan jika ada"
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-24"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Tanggal booking</label>
              <Field name="bookingDate">
                {({ field, form }: any) => (
                  <input
                    {...field}
                    type="date"
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    onChange={(e) => {
                      form.setFieldValue('bookingDate', e.target.value);
                      fetchSlots(e.target.value);
                    }}
                    // prevent choose date before today
                    min={new Date().toISOString().slice(0, 10)}
                  />
                )}
              </Field>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pilih Sesi yang tersedia
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {(fetchedSessions ?? availableSessions).map((s, idx) => (
                  <label key={s.id} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={values.sessions.includes(s.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        const next = [...values.sessions];
                        if (checked && !next.includes(s.id)) next.push(s.id);
                        if (!checked) {
                          const i = next.indexOf(s.id);
                          if (i > -1) next.splice(i, 1);
                        }
                        setFieldValue('sessions', next);
                      }}
                      className="w-4 h-4 text-primary-500 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      Sesi {idx + 1} {s.startTime.slice(0, 5)}-{s.endTime.slice(0, 5)}
                    </span>
                  </label>
                ))}
              </div>
              <ErrorMessage name="sessions" component="div" className="text-red-500 text-xs mt-1" />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSubmitting}>
                Add Schedule
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ScheduleModal;
